import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Typography, Descriptions, Tag, Space, Card, Divider, List } from "antd";
import type { AITool, FormFieldDefinition, AIActionButton, SelectOption } from "../../types/aiTools";
import { FIELD_TYPES } from "../../types/aiTools";

const { Text } = Typography;

export const AIToolShow = () => {
  const { query, result: record } = useShow<AITool>();
  const isLoading = query.isLoading;

  const getFieldTypeLabel = (type: string) => {
    return FIELD_TYPES.find((f) => f.value === type)?.label || type;
  };

  return (
    <Show isLoading={isLoading} title={`Narzędzie: ${record?.title || ""}`}>
      {record && (
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
          <Card>
            <Descriptions title="Podstawowe informacje" bordered column={2}>
              <Descriptions.Item label="ID">
                <Text copyable code>
                  {record.id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tytuł">{record.title}</Descriptions.Item>
              <Descriptions.Item label="Opis" span={2}>
                {record.description}
              </Descriptions.Item>
              <Descriptions.Item label="Ikona">
                <Text code>{record.icon}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Kategoria">
                <Tag
                  style={{
                    backgroundColor: `#${record.categoryColor}20`,
                    color: `#${record.categoryColor}`,
                  }}
                >
                  {record.category}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tagi">
                <Space size={4}>
                  {record.tags.map((tag: string) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Min. plan">
                {record.minPlanRequired === 1
                  ? "Free"
                  : record.minPlanRequired === 2
                    ? "Pro"
                    : "Enterprise"}
              </Descriptions.Item>
              <Descriptions.Item label="Executions/miesiąc">
                {record.executionsPerMonth}
              </Descriptions.Item>
              <Descriptions.Item label="Użycia (łącznie)">{record.usageCount}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="AI">
            <Descriptions.Item label="Instrukcje AI">
              {record.aiSystemInstruction}
            </Descriptions.Item>
            <Descriptions.Item label="Prompt AI">
                {record.aiPrompt}
              </Descriptions.Item>
          </Card>

          <Card title="Definicja formularza">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Opis">
                {record.formTemplate.description || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Pozwól na załączniki">
                <Tag color={record.formTemplate.allowAttachments ? "green" : "red"}>
                  {record.formTemplate.allowAttachments ? "Tak" : "Nie"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Linkowanie aukcji">
                <Tag color={record.formTemplate.allowAuctionLinking ? "green" : "red"}>
                  {record.formTemplate.allowAuctionLinking ? "Tak" : "Nie"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Własne profile">
                <Tag color={record.formTemplate.allowCustomProfiles ? "green" : "red"}>
                  {record.formTemplate.allowCustomProfiles ? "Tak" : "Nie"}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Pola formularza ({record.formTemplate.fields.length})</Divider>

            <List
              dataSource={[...record.formTemplate.fields].sort(
                (a: FormFieldDefinition, b: FormFieldDefinition) => (a.order || 0) - (b.order || 0)
              )}
              renderItem={(field: FormFieldDefinition) => (
                <List.Item>
                  <Card size="small" style={{ width: "100%" }}>
                    <Descriptions column={4} size="small">
                      <Descriptions.Item label="ID">
                        <Text code>{field.id}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Label">{field.label}</Descriptions.Item>
                      <Descriptions.Item label="Typ">
                        <Tag>{getFieldTypeLabel(field.fieldType)}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Wymagane">
                        <Tag color={field.required ? "red" : "default"}>
                          {field.required ? "Tak" : "Nie"}
                        </Tag>
                      </Descriptions.Item>
                      {field.placeholder && (
                        <Descriptions.Item label="Placeholder">{field.placeholder}</Descriptions.Item>
                      )}
                      {field.description && (
                        <Descriptions.Item label="Opis">{field.description}</Descriptions.Item>
                      )}
                      {field.defaultValue !== undefined && (
                        <Descriptions.Item label="Domyślna wartość">
                          <Text code>{JSON.stringify(field.defaultValue)}</Text>
                        </Descriptions.Item>
                      )}
                      {field.selectOptions && field.selectOptions.length > 0 && (
                        <Descriptions.Item label="Opcje" span={4}>
                          <Space size={4} wrap>
                            {field.selectOptions.map((opt: SelectOption) => (
                              <Tag key={opt.value}>
                                {opt.label} ({opt.value})
                              </Tag>
                            ))}
                          </Space>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </List.Item>
              )}
            />
          </Card>

          <Card title="Canvas Template">
            <Descriptions column={3} bordered size="small">
              <Descriptions.Item label="Typ">{record.canvasTemplate.type}</Descriptions.Item>
              <Descriptions.Item label="Dynamiczny">
                <Tag color={record.canvasTemplate.isDynamic ? "blue" : "default"}>
                  {record.canvasTemplate.isDynamic ? "Tak" : "Nie"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Prompt AI">
                {record.aiPrompt}
              </Descriptions.Item>
              <Descriptions.Item label="Edytowalne pola" span={2}>
                <Space size={4}>
                  {record.canvasTemplate.editableFields.map((f: string) => (
                    <Tag key={f}>{f}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {record.canvasTemplate.canvasSettings && (
              <>
                <Divider>Canvas Settings</Divider>
                <Descriptions column={3} size="small">
                  {record.canvasTemplate.canvasSettings.brandColor && (
                    <Descriptions.Item label="Brand color">
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          backgroundColor: record.canvasTemplate.canvasSettings.brandColor,
                          borderRadius: 4,
                          display: "inline-block",
                          marginRight: 8,
                        }}
                      />
                      {record.canvasTemplate.canvasSettings.brandColor}
                    </Descriptions.Item>
                  )}
                  {record.canvasTemplate.canvasSettings.fontFamily && (
                    <Descriptions.Item label="Font family">
                      {record.canvasTemplate.canvasSettings.fontFamily}
                    </Descriptions.Item>
                  )}
                  {record.canvasTemplate.canvasSettings.baseFontSize && (
                    <Descriptions.Item label="Base font size">
                      {record.canvasTemplate.canvasSettings.baseFontSize}px
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}
          </Card>

          <Card title={`Action Buttons (${record.actionButtons.length})`}>
            <List
              dataSource={[...record.actionButtons].sort(
                (a: AIActionButton, b: AIActionButton) => (a.order || 0) - (b.order || 0)
              )}
              renderItem={(btn: AIActionButton) => (
                <List.Item>
                  <Descriptions column={3} size="small">
                    <Descriptions.Item label="ID">
                      <Text code>{btn.id}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Label">{btn.label}</Descriptions.Item>
                    <Descriptions.Item label="Typ">
                      <Tag>{btn.actionType}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Opis">{btn.description}</Descriptions.Item>
                    <Descriptions.Item label="Wymaga input">
                      <Tag color={btn.requiresUserInput ? "orange" : "default"}>
                        {btn.requiresUserInput ? "Tak" : "Nie"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Dynamiczny">
                      <Tag color={btn.isDynamic ? "blue" : "default"}>
                        {btn.isDynamic ? "Tak" : "Nie"}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </List.Item>
              )}
            />
          </Card>
        </Space>
      )}
    </Show>
  );
};
